// Structured condition evaluator. Pure. No GameBoard state, no React.
// Conditions ride on automation entries (entry.condition) and gate
// whether the entry fires when its trigger matches.
//
// Implemented conditions:
//   'only_self_by_name'. Dark Tower / Gothic / Lone Tower cycle.
//   'controller_has_nearby'. The Empyrean. Args: { filters: [...] }.
//                              Satisfied if ANY filter matches a card
//                              the source's controller owns that is
//                              nearby the source. Each filter is the
//                              same shape scope.js uses: { subtype?,
//                              type?, status? }.
//   'hand_size_compare'. Queen of Midland. Compares two players'
//                              hand sizes via opts.handCountFor.
// Future conditions plug in here without touching GameBoard.

import { hasEffectiveStatus } from './scope';
import { effectiveSubtypes, isBrandedEvil, matchesFilter, nameWordsMatch } from './cardFilter';
import { effectivePower } from '../core/effectiveStats';
import { countsAsUnit } from '../core/typeline';
import { hasAirborne } from '../core/movement';
import { isBurrowed, isSubmerged } from '../core/burrowSubmerge';
import { effectiveSiteThresholds, isLandSiteAtCell, isWaterSiteAtCell } from '../core/regions';
import { hasFiredForCell, isCornerCell } from '../core/firstVisit';
import { cellMatchesPosition } from '../core/positional';
import { cellMatchesRemembered } from './ops/cards/sirPerceval';
import { anchorCoveredCells } from '../core/auraPlacement';
import { sameControllerSiteInCell } from './ops/cards/babelTower';
import { getCardCost } from '../core/casting';
import { expandSubtypeAliases } from './subtypeAliases';

/**
 * @param {Object|null} condition - { type: 'only_self_by_name' } or null.
 * @param {Object} sourceCard - the card whose automation is firing.
 * @param {Object} fullCard - the catalog entry (for name).
 * @param {Iterable<[any, { userData: { cardInstance } }]>} meshes
 *   GameBoard.meshes or any compatible iterable.
 * @returns {boolean} true if the condition holds (effect should fire).
 */
export function evaluateCondition(condition, sourceCard, fullCard, meshes, opts = {}) {
  if (!condition) return true;
  if (condition.type === 'controller_has_other') {
    for (const [, mesh] of meshes) {
      const card = mesh?.userData?.cardInstance;
      if (card && matchesFilter(card, { ...condition.filter, controller: 'self', exclude_self: true }, { source: sourceCard })) return true;
    }
    return false;
  }

  // v1.77c. Compound AND. All sub-conditions must hold. Used by
  // cards combining gates (Plate of the Whale: bearer power >= 4
  // AND not used this turn).
  if (condition.type === 'all_of') {
    const subs = condition.conditions || [];
    return subs.every((c) => evaluateCondition(c, sourceCard, fullCard, meshes, opts));
  }
  if (condition.type === 'any_of') {
    const subs = condition.conditions || [];
    return subs.some((c) => evaluateCondition(c, sourceCard, fullCard, meshes, opts));
  }

  if (condition.type === 'only_self_by_name') {
    const name = fullCard?.name;
    if (!name) return true;
    let count = 0;
    for (const [, mesh] of meshes) {
      const c = mesh?.userData?.cardInstance;
      if (!c || c.rotated !== sourceCard.rotated) continue;
      if (c.name === name) count += 1;
      if (count > 1) return false; // short-circuit; >1 fails
    }
    return count === 1;
  }

  // The Empyrean pattern: at least one card the source controls is
  // nearby AND matches one of the listed filters. Each filter mirrors
  // scope.js: subtype / type / status. "Nearby" uses the same
  // 8-neighbor-plus-self rule as the scope resolver (v1.18a).
  if (condition.type === 'controller_has_nearby') {
    const filters = condition.filters || [];
    if (filters.length === 0) return false;
    const cellAt = opts.cellAt;
    if (!cellAt) return true; // no grid context. Fail open
    const sourceMesh = findMesh(meshes, sourceCard.id);
    if (!sourceMesh) return false;
    const sourceCell = cellAt(sourceMesh.position.x, sourceMesh.position.z);
    if (!sourceCell) return false;
    for (const [, mesh] of meshes) {
      const card = mesh?.userData?.cardInstance;
      if (!card || card.id === sourceCard.id) continue;
      if (card.rotated !== sourceCard.rotated) continue; // controller match
      const cell = cellAt(mesh.position.x, mesh.position.z);
      if (!cell) continue;
      const dx = Math.abs(cell.col - sourceCell.col);
      const dy = Math.abs(cell.row - sourceCell.row);
      if (dx > 1 || dy > 1) continue;
      if (filters.some((f) => matchesFilter(card, f))) return true;
    }
    return false;
  }

  // Thin Ice: "if minions atop this site total 5 or more power,
  // submerge them." Sums the effective power of filter-matching units
  // sharing the source's cell. The filter's region:'surface' token
  // skips anything already below the surface. Reusable for any
  // power-total-at-a-location gate.
  if (condition.type === 'scope_power_total_at_least') {
    const amount = condition.amount || 0;
    const cellAt = opts.cellAt;
    if (!cellAt) return false;
    const sourceMesh = findMesh(meshes, sourceCard.id);
    if (!sourceMesh) return false;
    const sourceCell = cellAt(sourceMesh.position.x, sourceMesh.position.z);
    if (!sourceCell) return false;
    const filter = condition.filter || { type: 'Minion' };
    const ctx = { getFullCard: opts.getFullCard };
    let total = 0;
    for (const [, mesh] of meshes) {
      const card = mesh?.userData?.cardInstance;
      if (!card || card.id === sourceCard.id) continue;
      const cell = cellAt(mesh.position.x, mesh.position.z);
      if (!cell || cell.col !== sourceCell.col || cell.row !== sourceCell.row) continue;
      if (!matchesFilter(card, filter, ctx)) continue;
      total += effectivePower(card);
    }
    return total >= amount;
  }

  // v1.77b. Once-per-turn self-gate. Pairs with the
  // MARK_USED_THIS_TURN op so cards like Tufted Turtles ("the first
  // time would-take-damage each turn") can self-limit. The key is a
  // per-card string label written by MARK_USED; we check whether
  // the source card's _abilitiesUsedThisTurn Set contains it.
  // End-phase cleanup (_stepPruneExpiredEffects) clears the Set for
  // the player whose turn just ended.
  if (condition.type === 'not_used_this_turn') {
    const key = condition.key;
    if (!key) return true; // missing key is fail-open
    const used = sourceCard._abilitiesUsedThisTurn;
    if (!used) return true;
    return !used.has(key);
  }

  // v1.77f. Once-per-game self-gate. Like not_used_this_turn but
  // the flag persists for the rest of the match (never cleared).
  // Altar of Malachai's "Once per game" gate.
  if (condition.type === 'not_used_this_game') {
    const key = condition.key;
    if (!key) return true;
    const used = sourceCard._abilitiesUsedThisGame;
    if (!used) return true;
    return !used.has(key);
  }

  // v1.77c. Damage-amount threshold. Used by Makeshift Barricade's
  // "if the damage was 3 or more, the barricade breaks." The
  // BANISH_SELF op inside the replacement entry gets its OWN
  // condition. The runtime passes the live damage amount via
  // opts.amount when evaluating per-effect conditions.
  if (condition.type === 'damage_amount_at_least') {
    const threshold = condition.amount || 0;
    return (opts.amount || 0) >= threshold;
  }

  // v1.77c. Bearer's effective power threshold. Plate of the Whale
  // pattern: "If it has more than 3 power, prevent the first instance
  // of damage..." Caller passes opts.bearer (the equipped artifact's
  // host) so we read its currentAttack + aura delta.
  if (condition.type === 'bearer_power_at_least') {
    const threshold = condition.amount || 0;
    const bearer = opts.bearer;
    if (!bearer) return false;
    const effective = (bearer.currentAttack ?? bearer.baseAttack ?? 0)
      + (bearer._auraStatDelta || 0);
    return effective >= threshold;
  }
  // Mirror of bearer_power_at_least. True when bearer's effective
  // power is <= the threshold. Plate of the Whale's "Bearer is
  // immobile if it has less than 3 power" → threshold 2.
  if (condition.type === 'bearer_power_at_most') {
    const threshold = condition.amount ?? Infinity;
    const bearer = opts.bearer;
    if (!bearer) return false;
    const effective = (bearer.currentAttack ?? bearer.baseAttack ?? 0)
      + (bearer._auraStatDelta || 0);
    return effective <= threshold;
  }
  // v1.80. Match the damage-source CARD against type / subtype /
  // power filters. Distinct from damage_source_type which inspects the
  // event tag list. Sir Galahad "no damage from Demons, Spirits, and
  // Undead" → { subtype: ['Demon','Spirit','Undead'] }. Fourberie Knot
  // "no damage from units with 4 or more power" → { power_at_least: 4 }.
  // Tragedy Worrywart "no damage from Magic spells" → { type: 'Magic' }.
  if (condition.type === 'damage_source_match') {
    const src = opts.sourceCard;
    if (!src) return false;
    if (condition.type_ && src.type !== condition.type_) return false;
    // condition uses `card_type` to avoid colliding with the
    // discriminator `type` field of the condition itself.
    if (condition.card_type && src.type !== condition.card_type) return false;
    if (condition.subtype) {
      const wanted = Array.isArray(condition.subtype) ? condition.subtype : [condition.subtype];
      // effectiveSubtypes (not raw subTypes) so a Corruptor-transformed
      // source matches by its current identity (Beast → Monster).
      const subs = effectiveSubtypes(src);
      const wantedLower = wanted.map((s) => String(s).toLowerCase());
      if (!wantedLower.some((w) => subs.includes(w))) return false;
    }
    if (typeof condition.power_at_least === 'number') {
      const effective = (src.currentAttack ?? src.baseAttack ?? 0)
        + (src._auraStatDelta || 0);
      if (effective < condition.power_at_least) return false;
    }
    return true;
  }

  // v1.77d. Damage source typing. opts.sourceType is an array of
  // tags describing the hit. Combat strikes carry 'strike' or 'ranged';
  // tags include each lowercase element of the source card (so a fire
  // creature's strike is ['strike','fire']). Spell damage carries
  // 'spell' plus the spell's elements. Askelon Phoenix uses
  // { type: 'damage_source_type', any: ['fire'] }. True if ANY tag
  // in opts.sourceType is in condition.any.
  if (condition.type === 'damage_source_type') {
    const wanted = condition.any || [];
    const have = opts.sourceType;
    if (wanted.length === 0 || !have) return false;
    const haveArr = Array.isArray(have) ? have : [have];
    return wanted.some((t) => haveArr.includes(t));
  }

  // v1.95. Filter on the watch context's target card (the entering /
  // dying / struck card). Used by realm-entry watchers like Druid to
  // gate "enemy entered nearby" before firing. Sub-fields:
  //   enemy. True: opts.watchTarget.rotated differs from source's
  //   ally. True: opts.watchTarget.rotated matches source's
  //   near_source. True: opts.watchTarget is in 8-neighbor of source
  //   type. Exact type match
  //   subtype. String or array, case-insensitive
  //   not_site. True: opts.watchTarget.type !== 'Site' (Bottomless
  //                  Pit-style "kill non-Airborne entrants" exclusion).
  //   on_ground. True: watchTarget is NOT airborne, NOT burrowed,
  //                  and NOT submerged (Wall of Brambles: damage only
  //                  enemies traversing "on the ground").
  //   name. Exact name match (honours aura name aliases) OR
  //   name_words. String / array of whole-word matches. The Round
  //                  Table: name 'King Arthur' OR name_words [Knight,
  //                  Sir, Dame]. Either present is sufficient (OR), so
  //                  a card matching the name OR any listed word fires.
  if (condition.type === 'watch_target_filter') {
    const target = opts.watchTarget;
    if (!target) return false;
    // self. True: the watch target IS the source card (Sir Lancelot's
    // "untap him" fires only when HE was the one struck, not any unit).
    if (condition.self === true
        && (target.instanceId ?? target.id) !== (sourceCard.instanceId ?? sourceCard.id)) return false;
    if (condition.enemy === true && !!target.rotated === !!sourceCard.rotated) return false;
    if (condition.ally === true  && !!target.rotated !== !!sourceCard.rotated) return false;
    if (condition.type_ && target.type !== condition.type_) return false;
    if (condition.not_site === true && target.type === 'Site') return false;
    if (condition.on_ground === true
        && (hasAirborne(target) || isBurrowed(target) || isSubmerged(target))) return false;
    if (condition.name || condition.name_words) {
      const exactOk = condition.name
        && (target.name === condition.name
          || (target._auraNameAliases || []).includes(condition.name));
      // Whole-word with a plural fold (shared with cardFilter): the token
      // "Knight" matches "Vanguard Knights" but never "Knighthood".
      const wordsOk = condition.name_words
        ? nameWordsMatch(target.name, condition.name_words)
        : false;
      if (!exactOk && !wordsOk) return false;
    }
    if (condition.subtype) {
      const wanted = (Array.isArray(condition.subtype) ? condition.subtype : [condition.subtype])
        .map((s) => String(s).toLowerCase());
      const expanded = expandSubtypeAliases(wanted);
      // Canonical subtype read. Handles the catalog string / live
      // array shapes AND applies TRANSFORM_SUBTYPES aura swaps.
      const subs = effectiveSubtypes(target);
      const brandedEvil = wanted.includes('evil') && isBrandedEvil(target);
      if (!brandedEvil && !expanded.some((w) => subs.includes(w))) return false;
    }
    if (condition.near_source === true) {
      const cellAt = opts.cellAt;
      if (!cellAt) return false;
      const srcMesh = findMesh(meshes, sourceCard.id);
      const tgtMesh = findMesh(meshes, target.id);
      if (!srcMesh || !tgtMesh) return false;
      const sCell = cellAt(srcMesh.position.x, srcMesh.position.z);
      const tCell = cellAt(tgtMesh.position.x, tgtMesh.position.z);
      if (!sCell || !tCell) return false;
      const dx = Math.abs(sCell.col - tCell.col);
      const dy = Math.abs(sCell.row - tCell.row);
      if (dx > 1 || dy > 1) return false;
      // Codex "Nearby respects regions" (already honored for static aura
      // scopes): a buried/submerged watcher is not near a surface unit even
      // on the same cell. False Idol must not draw while buried (#33).
      const regionOf = (mesh, card) =>
        (mesh?.userData?.cardInstance?.subsurface ?? card?.subsurface ?? null);
      if (regionOf(srcMesh, sourceCard) !== regionOf(tgtMesh, target)) return false;
    }
    return true;
  }

  // Threshold-gated ability (Kingdom of Agartha: "(E)(E)(E). All minions
  // have Burrowing"): live while the source's CONTROLLER meets the printed
  // element threshold. A player's affinity is the sum of what their
  // controlled sites provide (effectiveSiteThresholds. Flooded floor,
  // provides stamps and suppression included). condition: { element, amount }.
  if (condition.type === 'affinity_at_least') {
    const element = condition.element;
    const amount = condition.amount || 0;
    if (!element) return false;
    const getFull = opts.getFullCard || (() => null);
    let total = 0;
    for (const [, mesh] of meshes) {
      const c = mesh?.userData?.cardInstance;
      if (!c || !(c.isSite || c.type === 'Site')) continue;
      if (!!c.rotated !== !!sourceCard?.rotated) continue;
      total += effectiveSiteThresholds(c, getFull(c.cardId) || c)[element] || 0;
      if (total >= amount) return true;
    }
    return total >= amount;
  }

  // Pendragon Banner: "Nearby allies have +1 power while there are no
  // enemies here." "Here" is the source's own square (the bearer's square
  // while carried); an enemy is any unit the opponent controls (codex
  // Enemy). Fails closed without a grid or a resolvable source cell.
  if (condition.type === 'no_enemy_at_source_cell') {
    const cellAt = opts.cellAt;
    if (!cellAt) return false;
    const mesh = findMesh(meshes, sourceCard?.id)
      || (opts.bearer ? findMesh(meshes, opts.bearer.id) : null);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) return false;
    const srcRotated = opts.bearer ? !!opts.bearer.rotated : !!sourceCard?.rotated;
    for (const [, m2] of meshes) {
      const c = m2?.userData?.cardInstance;
      if (!c || !countsAsUnit(c)) continue;
      if (!!c.rotated === srcRotated) continue;
      const cCell = cellAt(m2.position.x, m2.position.z);
      if (cCell && cCell.col === cell.col && cCell.row === cell.row) return false;
    }
    return true;
  }

  // v1.103. Source occupies a site with the named element affinity.
  // Avatar of Air: "if you occupy an Air site." args: { element: 'air' }.
  // opts.elementsAt(col,row) → { air, earth, fire, water } supplied by
  // GameBoard so the condition stays pure.
  if (condition.type === 'source_on_element_site') {
    const element = condition.element;
    if (!element) return false;
    const cellAt = opts.cellAt;
    const elementsAt = opts.elementsAt;
    if (!cellAt || !elementsAt) return false;
    const mesh = findMesh(meshes, sourceCard.id);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) return false;
    const totals = elementsAt(cell.col, cell.row);
    return (totals?.[element] || 0) > 0;
  }

  // Cell-type gate for `on_enter_location` triggers. Reads the cell
  // the watchTarget just entered (resolved via opts.cellAt) and asks
  // GameBoard's site-type probe whether it's the expected kind. Used
  // by:
  //   Mariner's Curse: { type: 'entered_cell_site_type', site_type: 'water' }
  //   Rest in Peace:   { type: 'entered_cell_site_type', site_type: 'land' }
  // GameBoard injects `opts.cellIsWaterSite` / `opts.cellIsLandSite`
  // (closures over the live meshes + catalog) so this stays pure.
  if (condition.type === 'entered_cell_site_type') {
    const target = opts.watchTarget;
    if (!target) return false;
    const cellAt = opts.cellAt;
    if (!cellAt) return false;
    const tgtMesh = findMesh(meshes, target.id);
    if (!tgtMesh) return false;
    const cell = cellAt(tgtMesh.position.x, tgtMesh.position.z);
    if (!cell) return false;
    if (condition.site_type === 'water') {
      return typeof opts.cellIsWaterSite === 'function'
        ? !!opts.cellIsWaterSite(cell.col, cell.row) : false;
    }
    if (condition.site_type === 'land') {
      return typeof opts.cellIsLandSite === 'function'
        ? !!opts.cellIsLandSite(cell.col, cell.row) : false;
    }
    return false;
  }

  // First-time-at-cell gate for `on_enter_location` / `on_stop`
  // triggers. Read-only: it never burns the flag. The paired
  // `mark_cell_visited` op does the write on resolution so an optional
  // "may" the player declines keeps the first-time intact. The relevant
  // cell is always the SOURCE's own cell (for on_enter_location the
  // entering unit lands AT the source's cell; for on_stop the source
  // IS the mover). Sub-fields:
  //   per_cell. False (default): single-shot boolean. Vaults of
  //                  Zul "the first time an Avatar stops here".
  //                  true: one shot per distinct cell. Wayfaring
  //                  Pilgrim "each corner of the realm for the first
  //                  time".
  //   corner_only. True: the source cell must be a grid corner
  //                  (opts.gridDims supplies cols/rows). Used with
  //                  per_cell for Wayfaring Pilgrim.
  if (condition.type === 'first_visit_to_cell') {
    const cellAt = opts.cellAt;
    if (!cellAt) return false;
    const mesh = findMesh(meshes, sourceCard?.id);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) return false;
    if (condition.corner_only === true) {
      const grid = opts.gridDims;
      if (!grid || !isCornerCell(cell, grid)) return false;
    }
    return !hasFiredForCell(sourceCard, cell, condition.per_cell === true);
  }

  // General board-position gate. `where` is one of corner / outer_column /
  // outer_row / edge / back_row / front_row (positional.cellMatchesPosition).
  // back_row / front_row are perspective-relative. Derived from the
  // source's own controller (`sourceCard.rotated`). Reads the source's live
  // cell via opts.cellAt; fails closed without a grid or cell. Lets cards
  // like Forsaken ("cast to an outer column") or the back-row sites express
  // their gate as data instead of bespoke code.
  if (condition.type === 'position_at') {
    const cellAt = opts.cellAt;
    const grid = opts.gridDims;
    if (!cellAt || !grid) return false;
    const mesh = findMesh(meshes, sourceCard?.id);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) return false;
    return cellMatchesPosition(condition.where, cell, grid, !!sourceCard?.rotated);
  }

  // Sir Perceval's `on_stop` gate: the source's current cell equals the
  // cell the opponent stamped on it during Genesis (`_percevalTargetCell`).
  // "When Sir Perceval enters that site, draw a card." Reads the source's
  // live cell via opts.cellAt; fails closed when no pick is remembered.
  if (condition.type === 'source_at_remembered_cell') {
    const cellAt = opts.cellAt;
    if (!cellAt) return false;
    const mesh = findMesh(meshes, sourceCard?.id);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    return cellMatchesRemembered(sourceCard, cell);
  }

  // Reason gate for `on_enter_location` triggers. The same trigger
  // fires for both fresh summons (a unit played onto a covered cell)
  // and intra-grid moves (a unit stepping onto a covered cell). Cards
  // whose rules text reads "When you SUMMON X here" (Evil Presence)
  // must NOT fire when an existing unit merely moves in. Unlike
  // "whenever a minion ENTERS" cards (Mariner's Curse) which fire on
  // both. opts.enterReason is 'summon' | 'move'; defaults to 'move'
  // so any caller that doesn't thread a reason can't accidentally
  // satisfy a summon-only gate.
  if (condition.type === 'enter_reason') {
    return (opts.enterReason || 'move') === condition.reason;
  }

  // Gate on the SOURCE's current site being water or land. Codex
  // Underwater: only water sites have an underwater region, so
  // submerge-granting effects are impossible elsewhere. Vatn
  // Draconis: "you may submerge all minions and artifacts here" only
  // fires while she sits on a water site (water affinity > 0,
  // including Flooded). condition: { site: 'water' | 'land' }
  if (condition.type === 'source_on_site_type') {
    const mesh = findMesh(meshes, sourceCard?.id);
    if (!mesh || !opts.cellAt) return false;
    const cell = opts.cellAt(mesh.position.x, mesh.position.z);
    if (!cell) return false;
    const getFull = opts.getFullCard || (() => null);
    if (condition.site === 'water') {
      return isWaterSiteAtCell(meshes, opts.cellAt, cell.col, cell.row, getFull);
    }
    if (condition.site === 'land') {
      return isLandSiteAtCell(meshes, opts.cellAt, cell.col, cell.row, getFull);
    }
    return false;
  }

  // Xeraphine Konrul: "choose three of your dead minions." The
  // end-of-turn prompt only fires when the controller's cemetery
  // holds at least `amount` cards matching `filter`. The caller
  // supplies the cemetery through opts.cemeteryCardsFor (pile rows
  // carry no type; opts.getFullCard resolves the catalog card).
  if (condition.type === 'cemetery_count_at_least') {
    const controller = sourceCard?.rotated ? 'p2' : 'p1';
    const cards = opts.cemeteryCardsFor ? (opts.cemeteryCardsFor(controller) || []) : [];
    const getFull = opts.getFullCard || (() => null);
    const matching = cards.filter((c) => matchesFilter(getFull(c.cardId) || c, condition.filter || {}));
    return matching.length >= (condition.amount || 1);
  }

  // Compare two players' hand sizes. Queen of Midland's draw gate
  // "an opponent has more cards than you" → subject 'opponent',
  // op 'greater_than', vs 'controller'. `subject` and `vs` resolve to a
  // hand count via opts.handCountFor(player); 'controller' is the
  // source's owner, 'opponent' the other seat. `vs` may instead be a
  // literal number for "more than N cards" style gates.
  if (condition.type === 'hand_size_compare') {
    const handCountFor = opts.handCountFor;
    if (!handCountFor) return false;
    const controller = sourceCard?.rotated ? 'p2' : 'p1';
    const opponent = controller === 'p1' ? 'p2' : 'p1';
    const seatFor = (side) => (side === 'opponent' ? opponent : controller);
    const left = handCountFor(seatFor(condition.subject)) || 0;
    const right = typeof condition.vs === 'number'
      ? condition.vs
      : (handCountFor(seatFor(condition.vs)) || 0);
    if (condition.op === 'greater_than') return left > right;
    if (condition.op === 'at_least') return left >= right;
    if (condition.op === 'less_than') return left < right;
    return false;
  }

  // Compare the controller's controlled-site count to opponents'.
  // Primordial Spring's Genesis "if you control fewer sites than any
  // opponent" → op 'fewer_than', vs 'any_opponent'. opts.siteCountFor
  // maps a seat to its controlled-site count (mirrors handCountFor).
  // "fewer than any opponent" = controller < the max opponent count
  // (with two seats, < the single opponent's count).
  if (condition.type === 'site_count_compare') {
    const siteCountFor = opts.siteCountFor;
    if (!siteCountFor) return false;
    const controller = sourceCard?.rotated ? 'p2' : 'p1';
    const opponent = controller === 'p1' ? 'p2' : 'p1';
    const mine = siteCountFor(controller) || 0;
    const theirs = siteCountFor(opponent) || 0;
    if (condition.op === 'fewer_than') return mine < theirs;
    if (condition.op === 'at_least') return mine >= theirs;
    return false;
  }

  // Source minion currently carries an equipped artifact. Sir
  // Bedivere's "if Sir Bedivere is carrying an artifact." Equipment
  // lives on the host instance's `equippedCards[]`; the default filter
  // gates to artifacts, resolved through opts.getFullCard for the
  // catalog type.
  if (condition.type === 'carrying_artifact') {
    const carried = sourceCard?.equippedCards || [];
    if (carried.length === 0) return false;
    const filter = condition.filter || { type: 'Artifact' };
    const getFull = opts.getFullCard || (() => null);
    return carried.some((c) => matchesFilter(getFull(c.cardId) || c, filter));
  }

  // on_spell_cast gate: the cast spell (carried in opts.watchTarget)
  // matches `condition.filter`, and. When `in_covered` is set. Was
  // cast to a cell this aura source covers. Sylvan Splendor: "Whenever
  // you cast a Beast or Faerie to an affected site, draw a spell."
  // The cast spell's cell is resolved from its live mesh via opts.cellAt
  // (the spell is on the board when on_spell_cast fires).
  if (condition.type === 'cast_spell_filter') {
    const spell = opts.watchTarget;
    if (!spell) return false;
    const ctx = { source: sourceCard, getFullCard: opts.getFullCard };
    if (condition.filter && !matchesFilter(spell, condition.filter, ctx)) return false;
    if (condition.in_covered === true) {
      const anchor = sourceCard?._auraAnchor;
      const shape = sourceCard?._auraShape?.shape;
      const cellAt = opts.cellAt;
      if (!anchor || !shape || !cellAt) return false;
      const covered = new Set(anchorCoveredCells(anchor, shape));
      const mesh = findMesh(meshes, spell.id);
      if (!mesh) return false;
      const cell = cellAt(mesh.position.x, mesh.position.z);
      if (!cell || !covered.has(`${cell.col},${cell.row}`)) return false;
    }
    return true;
  }

  // Watch-target (the struck card from a combat event) sits on a cell this
  // aura source covers. Sylvan Splendor: "Dispel if an affected site is
  // successfully attacked." Mirrors cast_spell_filter's in_covered block.
  if (condition.type === 'watch_target_in_covered') {
    const struck = opts.watchTarget;
    const anchor = sourceCard?._auraAnchor;
    const shape = sourceCard?._auraShape?.shape;
    const cellAt = opts.cellAt;
    if (!struck || !anchor || !shape || !cellAt) return false;
    // The watched event is a SITE being hit ("Dispel if an affected site is
    // successfully attacked." Sylvan Splendor, the only consumer). A minion
    // struck while standing on a covered site is not an affected site.
    if (!(struck.isSite || struck.type === 'Site')) return false;
    const covered = new Set(anchorCoveredCells(anchor, shape));
    const mesh = findMesh(meshes, struck.id);
    if (!mesh) return false;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    return !!cell && covered.has(`${cell.col},${cell.row}`);
  }

  // on_spell_cast gate: the cast spell's printed mana cost equals the
  // source card's current level-counter count (`_counters`, default 0).
  // The Immortal Throne: "Whenever anyone plays a card with cost equal
  // to the number of level counters on The Immortal Throne, ..."
  if (condition.type === 'cast_cost_equals_counter') {
    const spell = opts.watchTarget;
    if (!spell) return false;
    const getFull = opts.getFullCard || (() => null);
    const fullSpell = getFull(spell.cardId) || spell;
    const cost = getCardCost(fullSpell).mana;
    return cost === (sourceCard._counters || 0);
  }

  // on_kill gate: the minion just killed was the strongest enemy minion
  // (highest effective power) at the moment the fight began. The combat
  // engine snapshots enemy powers before damage and reports the verdict
  // via opts.killedWasStrongestEnemy. The killed card is off the board by
  // the time on_kill fires, so its power can't be re-read here. Sir
  // Pellinore: "Whenever Sir Pellinore kills the strongest enemy minion,
  // draw a card."
  if (condition.type === 'killed_was_strongest_enemy') {
    return !!opts.killedWasStrongestEnemy;
  }

  // on_death gate: the source died from damage redirected to it (Sir
  // Gawain's "takes damage for other adjacent allies"). applyRedirectedDamage
  // stamps `_tookRedirectedDamage` on the redirect target; the flag clears at
  // the end-phase heal alongside the damage. Sir Gawain: "If he dies from
  // damage taken this way, draw a card."
  if (condition.type === 'died_from_redirected_damage') {
    return !!sourceCard?._tookRedirectedDamage;
  }

  // The Apex of Babel Genesis gate: the source Site (the Apex) was played
  // onto the SAME square as a controlled Site with the named name (The Base
  // of Babel), merging them into The Tower of Babel. Codex: The Base of
  // Babel reads "You may build The Tower of Babel by playing The Apex of
  // Babel atop this." args: { name: 'The Base of Babel' }.
  if (condition.type === 'played_atop_named_site') {
    return !!sameControllerSiteInCell(condition.name, sourceCard, meshes, opts.cellAt);
  }

  // Unknown condition types pass. Better to fire than to silently
  // suppress a card whose data added a new condition we haven't
  // implemented yet.
  return true;
}

function findMesh(meshes, id) {
  for (const [, mesh] of meshes) {
    if (mesh?.userData?.cardInstance?.id === id) return mesh;
  }
  return null;
}

// matchesFilter for the controller_has_nearby check is now imported
// from `./cardFilter`. Status checks honour aura grants via the
// hasEffectiveStatus passed in ctx.
